"use client"

import React, { MouseEvent, useState } from "react"
import { useParams, usePathname, useSearchParams } from "next/navigation"

import { cn } from "@/lib/utils"
import { Link, ToggleMenuIcon } from "@/components/ui/atoms"

type TNavItem = {
  codename: string
  title: string
  href: string
  isAll?: boolean
  subitems?: TNavItem[]
}

type TMainNavigationMenuProps = {
  className?: string
  navItems: TNavItem[]
}

const MainNavigationMenu = ({
  navItems,
  className,
}: TMainNavigationMenuProps) => {
  // Retrieve the current path for active state checks
  const currentPath = usePathname()
  const params = useParams()
  const searchParams = useSearchParams()

  // Determine if a navigation item is active
  const isItemActive = (item: TNavItem) => {
    const itemPathWithParams = Object.keys(params).reduce((path, param) => {
      const paramValue = params[param]
      // Check if paramValue is an array and convert to string, otherwise, use as is.
      const value = Array.isArray(paramValue) ? paramValue.join("") : paramValue
      // Make sure to replace only if value is a string to satisfy TypeScript
      if (typeof value === "string") {
        return path.replace(`[${param}]`, value)
      }
      return path // If value is not a string, just return the path as is.
    }, item.href)

    const itemSearchString = searchParams.toString()

    // Check if the pathname matches and the search strings match if they exist
    return (
      currentPath === `/${itemPathWithParams}` &&
      (!itemSearchString || itemSearchString === itemSearchString)
    )
  }

  // Find the index of the initially active submenu item
  const initialActiveIndex = navItems.findIndex(isItemActive)

  const [activeSubMenuIndex, setActiveSubMenuIndex] =
    useState(initialActiveIndex)

  const handleSubMenuToggle =
    (index: number) => (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      // Toggle active index or reset if the same index is clicked again
      setActiveSubMenuIndex((prevIndex) => (prevIndex === index ? -1 : index))
    }

  return (
    <nav className={cn("main-navigation", className)}>
      <ul className="main-navigation__list">
        {navItems
          ? navItems.map((item, index) => {
              const isActive = isItemActive(item)
              const isSubMenuOpen = activeSubMenuIndex === index
              const isLastMainItem = index === navItems.length - 1
              return (
                <li
                  key={item.codename}
                  className={cn("main-navigation__list-item", {
                    "main-navigation__list-item--active": isActive,
                    "main-navigation__list-item--submenu-open": isSubMenuOpen,
                    "main-navigation__list-item--last": isLastMainItem,
                  })}
                >
                  <div
                    className={cn("main-navigation__link-group", {
                      "main-navigation__link-group--is-last": isLastMainItem,
                    })}
                  >
                    {item.subitems ? (
                      <ToggleMenuIcon
                        pressed={isSubMenuOpen}
                        onClick={handleSubMenuToggle(index)}
                        className="w-full"
                        aria-controls={`submenu-${index}`}
                        aria-expanded={isSubMenuOpen ? "true" : "false"}
                        title={item.title}
                      />
                    ) : null}
                  </div>
                  {item.subitems && isSubMenuOpen ? (
                    <section
                      id={`submenu-${index}`}
                      className={cn("main-navigation__submenu-container", {
                        "main-navigation__submenu-container--is-open":
                          isSubMenuOpen,
                      })}
                    >
                      <div className="main-navigation__submenu-container__inner">
                        <ul className="main-navigation__submenu">
                          {item.subitems
                            ? item.subitems.map((subitem) => {
                                const subPathUrl = `${subitem.href}`
                                return (
                                  <li
                                    key={subitem.codename}
                                    className={cn(
                                      "main-navigation__submenu-item",
                                      {
                                        "main-navigation__submenu-item--is-active":
                                          isSubMenuOpen ||
                                          currentPath === subitem.href,
                                      }
                                    )}
                                  >
                                    <Link
                                      chapters={[
                                        "Navigation",
                                        item.title,
                                        subitem.title,
                                      ]}
                                      variant="ghost"
                                      href={subPathUrl}
                                      className={cn(
                                        "main-navigation__submenu-link font-400",
                                        {
                                          "main-navigation__submenu-link--is-active":
                                            currentPath === subitem.href,
                                        }
                                      )}
                                      id={subitem.codename}
                                    >
                                      <span className={cn("paragraph-s")}>
                                        {subitem.title}
                                      </span>
                                    </Link>
                                  </li>
                                )
                              })
                            : null}
                          <li
                            key={"last-subItem"}
                            className={cn(
                              "main-navigation__submenu-item main-navigation__submenu-item--last",
                              {
                                "main-navigation__submenu-item--is-active":
                                  isSubMenuOpen || currentPath === item.href,
                              }
                            )}
                          >
                            <Link
                              chapters={["Navigation", item.title, item.title]}
                              variant="ghost"
                              iconRight={"ArrowRight"}
                              href={item.href}
                              className={cn(
                                "main-navigation__submenu-link font-500 underline",
                                {
                                  "main-navigation__submenu-link--is-active":
                                    currentPath === item.href,
                                }
                              )}
                              id={item.codename}
                            >
                              <span
                                className={cn(
                                  "paragraph-s paragraph-s--semibold"
                                )}
                              >
                                Accéder à tous les articles &quot;{item.title}
                                &quot;
                              </span>
                            </Link>
                          </li>
                        </ul>
                      </div>
                      <span
                        className="fixed z-10 hidden h-dvh w-dvw xl:block"
                        role="button"
                        tabIndex={0}
                        aria-label="Fermer le sous-menu"
                        onClick={handleSubMenuToggle(index)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault()
                            setActiveSubMenuIndex((prevIndex) =>
                              prevIndex === index ? -1 : index
                            )
                          }
                        }}
                      ></span>
                    </section>
                  ) : null}
                </li>
              )
            })
          : null}
      </ul>
    </nav>
  )
}

export { MainNavigationMenu, type TMainNavigationMenuProps, type TNavItem }
